import { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifyMfaPage from './pages/VerifyMfaPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
// @feature:oauthServer:start
import AuthorizePage from './pages/AuthorizePage';
// @feature:oauthServer:end
import HomePage from './pages/HomePage';
import SettingsPage from './pages/SettingsPage';
import RequireAuth from './lib/RequireAuth';
import LegalPage from './pages/LegalPage';
import SecurityPage from './pages/SecurityPage';
import AbusePage from './pages/AbusePage';
import SiteHeader from './components/Layout/SiteHeader';
import SiteFooter from './components/Layout/SiteFooter';
import CreatePage from './pages/CreatePage';
import DropReceivePage from './pages/DropReceivePage';
import DropStatusPage from './pages/DropStatusPage';
import FilesReceivePage from './pages/FilesReceivePage';
import HandoffPage from './pages/HandoffPage';
import LiveSendPage from './pages/LiveSendPage';
import HowItWorksPage from './pages/HowItWorksPage';
import LandingPage from './pages/LandingPage';
import RevealPage from './pages/RevealPage';
import SendPage from './pages/SendPage';
import StatusPage from './pages/StatusPage';
import BuyPassPage from './pages/BuyPassPage';
import PassBoughtPage from './pages/PassBoughtPage';
import ToolsPage from './pages/ToolsPage';
import { CONTENT_ID, focusContent } from './lib/pageFocus';

export default function App() {
  const navigate = useNavigate();

  // Desktop: `sss://auth/callback?…` deep links (OAuth return) arrive via the
  // Electron bridge — route them like any in-app navigation.
  useEffect(() => window.electronAPI?.deepLink.onNavigate((path) => navigate(path)), [navigate]);

  // After an in-app navigation, start keyboard and screen-reader users at the
  // new page's heading instead of wherever the old page left focus. Not on
  // the first load: the browser already starts at the top.
  const { pathname } = useLocation();
  const firstPath = useRef(pathname);
  useEffect(() => {
    if (pathname === firstPath.current) return;
    firstPath.current = '';
    focusContent();
  }, [pathname]);

  return (
    <>
      <SiteHeader />
      <div id={CONTENT_ID}>
        <Routes>
          {/* Public: the free product needs no account. */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/send" element={<SendPage />} />
          <Route path="/new" element={<CreatePage />} />
          <Route path="/s/:id" element={<RevealPage />} />
          <Route path="/live" element={<LiveSendPage />} />
          <Route path="/h/:id" element={<HandoffPage />} />
          <Route path="/f/:id" element={<FilesReceivePage />} />
          <Route path="/d/:id" element={<DropReceivePage />} />
          <Route path="/drop-status/:id" element={<DropStatusPage />} />
          <Route path="/status/:id" element={<StatusPage />} />
          <Route path="/pass/buy" element={<BuyPassPage />} />
          <Route path="/pass/bought" element={<PassBoughtPage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/privacy" element={<LegalPage doc="privacy" />} />
          <Route path="/terms" element={<LegalPage doc="terms" />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/abuse" element={<AbusePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/verify-mfa" element={<VerifyMfaPage />} />
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />
          {/* @feature:oauthServer:start */}
          {/* OAuth Authorization Server login/consent (MCP clients). Public: it signs in inline. */}
          <Route path="/authorize" element={<AuthorizePage />} />
          {/* @feature:oauthServer:end */}
          <Route
            path="/account"
            element={
              <RequireAuth>
                <HomePage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings/:tab"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <SiteFooter />
    </>
  );
}
